import React, { useEffect, useMemo, useState } from 'react';
import { Range } from 'rc-slider';
import { useDispatch, useSelector } from 'react-redux';
import { newCampaignActions } from '../../../../../store/newCampaign';

const AgeSegment = (props) => {
    const { isActive, calculatingForecast } = props;
    const dispatch = useDispatch();
    const newCampaign = useSelector((state) => state.newCampaign);
    const demographics = newCampaign.demographics;
    const segments = newCampaign.segments;
    const ages = demographics.segments.find((segment) => segment.id === 'age');
    const [marks, setMarks] = useState({});
    const [rangeValue, setRangeValue] = useState([0, 99]);
    const [min, setMin] = useState(0);
    const [max, setMax] = useState(99);
    const audienceProvider = demographics?.id || '';
    const ageRange = segments.age.sliderValue;

    const intervals = useMemo(() => {
        if (!ages?.keys?.length) {
            return [];
        }
        return ages.keys
            .map((key) => {
                const [fromRaw, toRaw] = key.id.split('-');
                const from = parseInt(fromRaw, 10);
                const to = parseInt(toRaw, 10);
                if (Number.isNaN(from) || Number.isNaN(to)) {
                    return null;
                }
                const backendTo =
                    audienceProvider === 'onemata' &&
                    from === 80 &&
                    to === 99
                        ? 199
                        : to;
                return {
                    id: key.id,
                    from,
                    to,
                    backendTo,
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.from - b.from);
    }, [ages, audienceProvider]);

    useEffect(() => {
        if (intervals.length === 0) {
            return;
        }
        const computedMin = intervals[0].from;
        const computedMax = intervals[intervals.length - 1].to;
        setMin(computedMin);
        setMax(computedMax);

        const computedMarks = {};
        intervals.forEach((interval, index) => {
            if (index === 0) {
                computedMarks[interval.from] = `${interval.from}`;
            }
            const label =
                interval.to === computedMax
                    ? `${interval.to}+`
                    : `${interval.to}`;
            computedMarks[interval.to] = label;
        });
        setMarks(computedMarks);

        const sanitizedRangeUnsorted =
            Array.isArray(ageRange) && ageRange.length === 2
                ? [
                      Math.min(
                          Math.max(computedMin, ageRange[0]),
                          computedMax
                      ),
                      Math.min(
                          Math.max(computedMin, ageRange[1]),
                          computedMax
                      ),
                  ]
                : [computedMin, computedMax];

        const sanitizedRange = sanitizedRangeUnsorted.sort(
            (a, b) => a - b
        );

        setRangeValue(sanitizedRange);
    }, [intervals, ageRange]);

    const arraysAreEqual = (first = [], second = []) =>
        first.length === second.length &&
        first.every((value, index) => value === second[index]);

    const changeAge = (selectedIds, sliderValue) => {
        if (calculatingForecast) {
            return;
        }
        const currentAge = segments.age || { value: [], sliderValue: [] };
        const currentValue = currentAge.value || [];
        const currentSlider = currentAge.sliderValue || [];

        if (
            arraysAreEqual(currentValue, selectedIds) &&
            arraysAreEqual(currentSlider, sliderValue)
        ) {
            return;
        }

        const updatedSegments = Object.assign({}, segments);
        updatedSegments.age = {
            value: selectedIds,
            sliderValue,
        };
        dispatch(newCampaignActions.setSegments(updatedSegments));
    };

    const handleChangeRange = (range) => {
        if (!Array.isArray(range) || range.length !== 2) {
            return;
        }
        const normalizedRange = [...range].sort((a, b) => a - b);
        setRangeValue(normalizedRange);

        if (calculatingForecast || intervals.length === 0) {
            return;
        }

        const adjustedUpperBound =
            audienceProvider === 'onemata' &&
            normalizedRange[1] >= max
                ? 199
                : normalizedRange[1];

        const selectedIds = intervals
            .filter(
                ({ from, backendTo }) =>
                    from >= normalizedRange[0] &&
                    backendTo <= adjustedUpperBound
            )
            .map(({ id, backendTo }) => {
                if (audienceProvider === 'onemata' && backendTo === 199) {
                    return '80-199';
                }
                return id;
            });

        const coversFullRange =
            normalizedRange[0] <= min && normalizedRange[1] >= max;

        changeAge(coversFullRange ? [] : selectedIds, normalizedRange);
    };

    return isActive ? (
        <div className="my-8 md:my-0">
            <Range
                min={min}
                max={max}
                value={rangeValue}
                step={null}
                marks={marks}
                onChange={handleChangeRange}
            />
        </div>
    ) : (
        ' '
    );
};

export default AgeSegment;
